import { SnapsnapGatewayBase } from './base'
import { Coupon } from '@/types/domain/order'
import { IIkkatsu, ISelflabo } from '@/entities/Cart'
import { ISubscription } from '@/types/domain/member'
import { IOrderProps, IOrderSeed, IOrderContextSeed, ICreditOrderResult, IPaypayOrderProps, IPaypayOrderSeed } from '@/entities/Order'
import { GetOrder, VerifyCouponCode, CreateOrder, CreateOrderWithContext, CreatePayPayOrder, CreateOrderContext } from '@/infra/network/Snapsnap/requests/order'
import { toOrderProps, toOrderSeed, toOrderContextSeed, toPayPayOrderSeed, toPayPayOrderProps } from './translator/order'
import { toSubscriptionSchema } from './translator/subscription'

export default class Order extends SnapsnapGatewayBase {
  async getOrder(orderId: number): Promise<IOrderProps> {
    const { order } = await this.apiClient.request(new GetOrder(orderId))
    return toOrderProps(order)
  }

  async verifyCouponCode(code: string): Promise<Coupon> {
    const { coupon } = await this.apiClient.request(new VerifyCouponCode(code))
    const { coupon_type, discount } = coupon

    return {
      type: coupon_type,
      amount: discount
    }
  }

  async createOrder(params: { seed: IOrderSeed; subscription: ISubscription; ikkatsu: IIkkatsu | null; selflabo: ISelflabo | null }): Promise<IOrderProps | ICreditOrderResult> {
    const { seed, subscription, ikkatsu, selflabo } = params
    const response = await this.apiClient.request(
      new CreateOrder({
        order: toOrderSeed(seed),
        subscription: toSubscriptionSchema(subscription),
        ikkatsu,
        selflabo
      })
    )

    // クレジット決済（Fincode）の場合、orderは返らずトップレベルに決済情報が返る
    if (response.fincode_order_id) {
      return {
        fincodeOrderId: response.fincode_order_id,
        redirectUrl: response.redirect_url || undefined,
        status: response.status || 'processing'
      }
    }

    return toOrderProps(response.order!)
  }

  // ctx付き注文作成（カード保存+決済フロー用）
  async createOrderWithContext(ctx: string): Promise<IOrderProps | ICreditOrderResult> {
    const response = await this.apiClient.request(new CreateOrderWithContext(ctx))

    if (response.fincode_order_id) {
      return {
        fincodeOrderId: response.fincode_order_id,
        redirectUrl: response.redirect_url || undefined,
        status: response.status || 'processing'
      }
    }

    return toOrderProps(response.order!)
  }

  async createPayPayOrder(params: { seed: IPaypayOrderSeed; subscription: ISubscription; ikkatsu: IIkkatsu | null; selflabo: ISelflabo | null }): Promise<IPaypayOrderProps> {
    const { seed, subscription, ikkatsu, selflabo } = params
    const response = await this.apiClient.request(
      new CreatePayPayOrder({
        order: toPayPayOrderSeed(seed),
        subscription: toSubscriptionSchema(subscription),
        ikkatsu,
        selflabo
      })
    )
    return toPayPayOrderProps(response)
  }

  async createOrderContext(params: { seed: IOrderContextSeed; subscription: ISubscription; ikkatsu: IIkkatsu | null; selflabo: ISelflabo | null }): Promise<{ contextToken: string }> {
    const { seed, subscription, ikkatsu, selflabo } = params
    const { context_token } = await this.apiClient.request(
      new CreateOrderContext({
        order: toOrderContextSeed(seed),
        subscription: toSubscriptionSchema(subscription),
        ikkatsu,
        selflabo
      })
    )
    return { contextToken: context_token }
  }
}
